// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryRowV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4 — sub-phase 4.6 (motion polish + token cleanup)
//
// Hairline conversation row per designer artboard `R5-0`. The row is a flat
// 4-column grid sitting directly on the page bone-deep surface — no card,
// no border around the row, no shadow. Only a 1px hairline at the bottom.
//
// Sub-phase 4.6 (THIS COMMIT):
//   • Replace `text-ellipsis whitespace-nowrap` on title + snippet with a
//     48px mask-image fade per `5P1-0` Variant 2. No `…` character anywhere.
//   • Drop redundant inline `style={{ fontFamily }}` paired with `font-sora`
//     / `font-mono` Tailwind classes — the Tailwind class already binds
//     the family (see tailwind.config.js → fontFamily.sora / fontFamily.mono).
//   • Add `cardReveal` stagger entry — each row delayed by `index * 0.04s`,
//     wired through `animationDelay` style. Reduced-motion users see no
//     animation (`motion-reduce:animate-none`).
//
// Sub-phase 4.4a:
//   • Drop the previous card shell (border / radius / shadow / min-h)
//   • Build the 4-column hairline grid
//   • Move machine label from above-title to right-side pill (with status dot)
//   • Restyle title typography (16/22 weight 500 → 15/18 weight 600)
//   • Bump page max-width and add active-row left-bar indicator (page-level)
//
// Sub-phase 4.4b (server-side fields wired):
//   • `last_message_preview` → 1-line snippet under title (Sora 13/19 #5A5147).
//     Server already cleans + truncates — render verbatim. Snippet element is
//     omitted entirely when `last_message_preview` is null (empty thread).
//   • `message_count` → "{N} msgs" in time sub-line (JBM 10/12 #9A9186).
//     Always render, including "0 msgs" so the column rhythm is preserved.
//
// Columns:
//   1. Time (64px fixed)         — JBM 12 weight 500 graphite + JBM 10 muted sub-line
//   2. Title + snippet (flex-1)  — Sora 15 weight 600 graphite + Sora 13 warm-stone
//   3. Machine pill (auto)       — pill with status dot + JBM 10 weight 500 mono name
//   4. Trailing arrow (16px)     — light stone stroke, warms to terracotta on hover
//
// Click → onOpen(threadId). Caller is responsible for navigation.
import React from "react";

import { Thread } from "../../../midasPageModels";

interface MidasHistoryRowV2Props {
  thread: Thread;
  /** Resolved machine display name ("Mazak QT-200") or null for platform threads. */
  machineLabel: string | null;
  /** Currently-loaded thread id (or null) — drives the left-bar active indicator. */
  activeThreadId: string | null;
  /** Click handler — opens the thread. */
  onOpen: (threadId: string) => void;
  /** Translation function — keeps the row stateless and i18n-safe. */
  t: (key: string, fallback: string) => string;
  /**
   * Sub-phase 4.6: zero-based row index used to stagger the entry animation
   * (`cardReveal` keyframe, `index * 0.04s` delay). The parent passes this so
   * the cascade is consistent within a date group; `motion-reduce` users see
   * no animation regardless. Default 0 keeps callers backwards-compatible.
   */
  index?: number;
}

/**
 * 48px mask-image fade per `5P1-0` Variant 2 (sub-phase 4.6, §3H.1).
 * Replaces the banned `text-ellipsis` + `…` character. The gradient applies
 * unconditionally — when content fits, the last 48px of the line just doesn't
 * carry pixels, so the fade has no visible effect. Both `mask-image` and
 * `WebkitMaskImage` are set for Safari/iOS support.
 */
const MASK_FADE_STYLE: React.CSSProperties = {
  maskImage:
    "linear-gradient(to right, black calc(100% - 48px), transparent)",
  WebkitMaskImage:
    "linear-gradient(to right, black calc(100% - 48px), transparent)",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

/** Format the time column — "HH:MM" only. Sub-line is now `{N} msgs`. */
const formatTime = (iso: string): string => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const MidasHistoryRowV2: React.FC<MidasHistoryRowV2Props> = ({
  thread,
  machineLabel,
  activeThreadId,
  onOpen,
  t,
  index = 0,
}) => {
  const time = formatTime(thread.last_message_at || thread.created_at);

  // 4.4b: server-derived message count shown in the time sub-line.
  // Render every value including 0 so the column rhythm stays consistent.
  const msgCountLabel = `${thread.message_count} msgs`;
  // 4.4b: server-cleaned + truncated preview. Already capped + ellipsised by
  // the Account microservice — render as-is. Null when the thread is empty
  // or cleaning produced empty text — in that case we omit the element.
  const preview = thread.last_message_preview;

  const isActive = activeThreadId !== null && activeThreadId === thread.id;
  const isPlatform = !thread.machine_id;
  // Status-dot color: terracotta for Platform Mode threads, green for any
  // machine-context thread. Per designer `R5-0`.
  const dotColor = isPlatform ? "#D4572B" : "#3FA26B";
  const pillLabel =
    machineLabel ||
    t("midas.historyPage.platformLabel", "PLATFORM").toUpperCase();

  const ariaLabel = `${
    machineLabel || t("midas.historyPage.platformMode", "Platform Mode")
  } — ${thread.title} — ${time} — ${msgCountLabel}`;

  // Sub-phase 4.6 — stagger entry. `cardReveal` is the shared keyframe injected
  // by `useRedesignSetup` (the page-level hook called by MidasHistoryPageV2);
  // we use it directly here. `motion-reduce:animate-none` respects
  // `prefers-reduced-motion: reduce`. We use inline `style` rather than a
  // Tailwind animate utility because the per-row `animationDelay` needs a
  // computed value (index * 0.04s).
  const animationStyle: React.CSSProperties = {
    animation: "cardReveal 320ms ease-out both",
    animationDelay: `${index * 0.04}s`,
  };

  return (
    <li
      className="relative motion-reduce:[animation:none!important]"
      style={animationStyle}
    >
      {/* Active-row left-bar indicator (3px terracotta). Lives outside the
          button so it overlays the row's hairline edge cleanly without
          fighting button padding. Conditional render keeps inactive rows
          paint-clean. */}
      {isActive && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-r-sm bg-midas-terracotta"
        />
      )}
      <button
        type="button"
        onClick={() => onOpen(thread.id)}
        aria-label={ariaLabel}
        aria-current={isActive ? "true" : undefined}
        title={thread.title}
        // Hairline row: NO card border / radius / shadow / min-h-76. Just a
        // 1px bottom hairline + py 14 / px 12 + 16px gap, sitting flat on the
        // page bone-deep surface. Hover bumps the row bg to a slightly
        // brighter bone tone (#FCFAF4) and warms the trailing arrow stroke
        // to terracotta — that's the entire hover treatment, no border /
        // shadow / scale.
        className="group flex w-full items-start gap-4 border-b border-solid border-[#EBE5D9] bg-transparent px-3 py-3.5 text-left outline-none transition-colors duration-150 hover:bg-[#FCFAF4] focus-visible:bg-[#FCFAF4] focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-1 focus-visible:ring-offset-midas-bone-deep motion-reduce:transition-none"
      >
        {/* ── Column 1: Time + sub-line (64px fixed) ──
            Top-aligned with a 2px nudge so the time baseline sits with
            the title's optical baseline, per the designer JSX (`pt-0.5`). */}
        <div className="flex w-16 flex-shrink-0 flex-col items-start pt-0.5">
          <span className="font-mono text-[12px] font-medium leading-4 tracking-[0.02em] text-[#1A1614]">
            {time}
          </span>
          {/* 4.4b: server-derived message count. Always rendered, including
              0, to keep the column rhythm consistent across all rows. */}
          <span className="font-mono text-[10px] leading-3 tracking-[0.04em] text-[#9A9186]">
            {msgCountLabel}
          </span>
        </div>

        {/* ── Column 2: Title + snippet (flex-1, min-w-0) ──
            4.4b: snippet element only renders when the server returned a
            non-null preview. Empty / cleaning-produced-empty threads omit
            the line — row height shrinks slightly, which is acceptable per
            spec §10. */}
        <div className="flex min-w-0 flex-grow flex-shrink basis-0 flex-col gap-1">
          {/* Sub-phase 4.6 §3H.1: 48px mask-fade, NO `text-ellipsis` / `…`.
              Per `5P1-0` Variant 2 (locked recommendation). */}
          <span
            className="block w-full font-sora text-[15px] font-semibold leading-[18px] tracking-[-0.005em] text-[#1A1614]"
            style={MASK_FADE_STYLE}
          >
            {thread.title}
          </span>
          {preview && (
            <p
              className="m-0 font-sora text-[13px] leading-[19px] text-[#5A5147]"
              style={MASK_FADE_STYLE}
            >
              {preview}
            </p>
          )}
        </div>

        {/* ── Column 3: Machine pill (auto width, top-aligned) ──
            Pill carries a 6px status dot (terracotta for Platform threads,
            green for machine-context threads) + the machine name (or
            "PLATFORM" when machine_id is null). */}
        <div className="mt-0.5 flex flex-shrink-0 items-center gap-1.5 self-start rounded-full border border-solid border-[#E2DDD3] bg-[#F4F1EC] px-2.5 py-1">
          <span
            aria-hidden="true"
            className="size-1.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
          <span className="font-mono text-[10px] font-medium leading-3 tracking-[0.04em] text-[#3D362F]">
            {pillLabel}
          </span>
        </div>

        {/* ── Column 4: Trailing arrow (16px) ──
            Plain 16×16 SVG, stroke #C5BDB1, strokeWidth 1.6, marginTop 4
            so it sits visually on the title baseline. Hover warms the
            stroke to terracotta + nudges 2px right. */}
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 4 }}
          className="text-[#C5BDB1] transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-midas-terracotta motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
        >
          <line x1="6" y1="12" x2="18" y2="12" />
          <polyline points="13 7 18 12 13 17" />
        </svg>
      </button>
    </li>
  );
};

export default MidasHistoryRowV2;
